import {
  ApiBlueprint,
  NotFoundErrorPage as NfsNotFoundErrorPageRef,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import {
  analyticsApiRef,
  configApiRef,
  createApiFactory,
  discoveryApiRef,
  errorApiRef,
  fetchApiRef,
  identityApiRef,
  oktaAuthApiRef,
  storageApiRef,
} from '@backstage/core-plugin-api';
import {
  AppRootWrapperBlueprint,
  IconBundleBlueprint,
  NavContentBlueprint,
  SignInPageBlueprint,
  SwappableComponentBlueprint,
  ThemeBlueprint,
} from '@backstage/plugin-app-react';
import {
  shortcutsApiRef,
  DefaultShortcutsApi,
} from '@backstage-community/plugin-shortcuts';
import { BackstageInsightsApi } from '@spotify/backstage-plugin-analytics-module-insights';
import {
  Analytics,
  type AnalyticsEventHandler,
  ClickEventHandler,
  DiscoverEventHandler,
  NavigateEventHandler,
  NotFoundEventHandler,
  SearchEventHandler,
  SignInEventHandler,
} from '@internal/plugin-neo4j-analytics';
import { backendAuthApiRef } from '@internal/plugin-neo4j-auth-common';
import { BackendAuthProd } from '../apis/BackendAuthProd';
import { BackendAuthNoop } from '../apis/BackendAuthNoop';
import { CombinedAnalyticsApi } from '../apis/CombinedAnalyticsApi';
import { signalApiRef } from '@backstage/plugin-signals-react';
import { UserSettingsStorage } from '@backstage/plugin-user-settings';
import ReorderIcon from '@material-ui/icons/Reorder';
import ListIcon from '@material-ui/icons/List';
import { NotFoundErrorPage } from '../components/errors/NotFoundErrorPage';
import { AuraSignInPage } from '../components/auth/AuraSignInPage';
import { RootProviders, SidebarContent } from '../components/Root';
import { AlmaPopUp } from '@internal/plugin-neo4j-alma';
import { SharedThemeProvider } from '../theme/SharedThemeProvider';

// This module override for pluginId:'app' is NFS's catch-all for
// app-wide concerns that don't belong to any plugin: themes, sign-in, icons,
// sidebar, root wrapper, and shared APIs (analytics, storage, auth,
// shortcuts shim).
export const appModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    // ── Themes (→ app theme system) ─────────────────────────
    // Upstream light+dark themes disabled in app-config.yaml; replaced by these 4 custom themes
    ThemeBlueprint.make({
      name: 'aura-light',
      params: {
        theme: {
          id: 'aura-light',
          title: 'Aura Light',
          variant: 'light',
          Provider: SharedThemeProvider,
        },
      },
    }),
    ThemeBlueprint.make({
      name: 'aura-dark',
      params: {
        theme: {
          id: 'aura-dark',
          title: 'Aura Dark',
          variant: 'dark',
          Provider: SharedThemeProvider,
        },
      },
    }),
    ThemeBlueprint.make({
      name: 'aura-tritanopia',
      params: {
        theme: {
          id: 'aura-tritanopia',
          title: 'Aura Tritanopia',
          variant: 'dark',
          Provider: SharedThemeProvider,
        },
      },
    }),
    ThemeBlueprint.make({
      name: 'aura-matrix',
      params: {
        theme: {
          id: 'aura-matrix',
          title: 'Aura Matrix',
          variant: 'dark',
          Provider: SharedThemeProvider,
        },
      },
    }),

    // ── Sign-in (→ app sign-in page) ────────────────────────
    SignInPageBlueprint.make({
      params: {
        loader: async () => AuraSignInPage,
      },
    }),

    // ── Icons (→ app icon registry) ─────────────────────────
    IconBundleBlueprint.make({
      name: 'aura-icons',
      params: {
        icons: {
          reorder: ReorderIcon,
          list: ListIcon,
        },
      },
    }),

    // ── Component override (→ replaces built-in component) ──
    SwappableComponentBlueprint.make({
      name: 'core-not-found-error-page',
      params: define =>
        define({
          component: NfsNotFoundErrorPageRef,
          loader: () => () => <NotFoundErrorPage />,
        }),
    }),

    // ── APIs (→ global API registry) ────────────────────────
    ApiBlueprint.make({
      name: 'analytics',
      params: defineParams =>
        defineParams(
          createApiFactory({
            api: analyticsApiRef,
            deps: {
              configApi: configApiRef,
              discoveryApi: discoveryApiRef,
              fetchApi: fetchApiRef,
              identityApi: identityApiRef,
              signalApi: signalApiRef,
              storageApi: storageApiRef,
            },
            factory: ({
              configApi,
              discoveryApi,
              fetchApi,
              identityApi,
              signalApi,
              storageApi,
            }) => {
              const analyticsEventHandlers: AnalyticsEventHandler[] = [
                new ClickEventHandler(),
                new DiscoverEventHandler(),
                new NavigateEventHandler(),
                new NotFoundEventHandler(),
                new SearchEventHandler(),
                new SignInEventHandler(),
              ];

              const ANALYTICS_TIMEOUT_MS = 1_000;

              /**
               * Wraps `fetchApi` with a timeout to prevent analytics requests
               * from holding browser connection slots.
               *
               * Browsers allow only 6 concurrent requests per origin —
               * slow analytics responses can starve real API calls.
               *
               * Requests that exceed the timeout are aborted; lost events are
               * acceptable since analytics is fire-and-forget.
               */
              const timedFetchApi = {
                fetch: (url: RequestInfo | URL, init?: RequestInit) => {
                  const controller = new AbortController();
                  const timer = setTimeout(
                    () => controller.abort(),
                    ANALYTICS_TIMEOUT_MS,
                  );

                  if (init?.signal) {
                    init.signal.addEventListener('abort', () =>
                      controller.abort(),
                    );
                  }

                  return fetchApi
                    .fetch(url, { ...init, signal: controller.signal })
                    .finally(() => clearTimeout(timer));
                },
              };

              /**
               * WORKAROUND: @backstage/plugin-signals WebSocket reconnect loop
               *
               * `SignalClient` closes the WebSocket when subscriptions hit 0.
               *
               * During startup, Insights `SessionManager` subscribes to the
               * "user-settings" signal channel (via `storageApi.observe$`) and
               * immediately unsubscribes after reading its cached session —
               * before the WebSocket finishes connecting.
               *
               * Closing a connecing socket produces a non-1000 close code,
               * which triggers reconnect(), looping every 5 s indefinitely.
               *
               * This no-op subscription keeps the count > 0 so the
               * socket is never torn down by a transient consumer.
               *
               * The below PRs by @drodil were never merged,
               * but Backstage are aware of the issue.
               *
               * @see https://github.com/backstage/backstage/pull/32495
               * @see https://github.com/backstage/backstage/pull/32692
               */
              signalApi?.subscribe('user-settings', () => {});

              return CombinedAnalyticsApi.fromApis([
                BackstageInsightsApi.fromConfig(configApi, {
                  discoveryApi,
                  fetchApi: timedFetchApi,
                  identityApi,
                  storageApi,
                }),
                Analytics.fromConfig(
                  configApi,
                  { discoveryApi, fetchApi: timedFetchApi },
                  analyticsEventHandlers,
                ),
              ]);
            },
          }),
        ),
    }),
    ApiBlueprint.make({
      name: 'backend-auth',
      params: defineParams =>
        defineParams(
          createApiFactory({
            api: backendAuthApiRef,
            deps: { configApi: configApiRef, okta: oktaAuthApiRef },
            factory: ({ configApi, okta }) => {
              const env = configApi.getOptionalString('auth.environment');
              if (env === 'ci') {
                return new BackendAuthNoop();
              }
              return new BackendAuthProd({ okta });
            },
          }),
        ),
    }),
    ApiBlueprint.make({
      name: 'storage',
      params: defineParams =>
        defineParams(
          createApiFactory({
            api: storageApiRef,
            deps: {
              discoveryApi: discoveryApiRef,
              errorApi: errorApiRef,
              fetchApi: fetchApiRef,
              identityApi: identityApiRef,
              signalApi: signalApiRef,
            },
            factory: deps => UserSettingsStorage.create(deps),
          }),
        ),
    }),
    // ── Root wrapper (→ app root) ───────────────────────────
    AppRootWrapperBlueprint.make({
      name: 'root-providers',
      params: {
        component: RootProviders,
      },
    }),

    // App root elements render on the sign-in page too. A wrapper renders only
    // after sign-in, so the Alma launcher stays off that page.
    AppRootWrapperBlueprint.make({
      name: 'alma-popup',
      params: {
        component: ({ children }) => (
          <>
            {children}
            <AlmaPopUp />
          </>
        ),
      },
    }),

    // ── Sidebar (→ app sidebar) ─────────────────────────────
    NavContentBlueprint.make({
      name: 'sidebar-content',
      params: {
        component: SidebarContent,
      },
    }),

    // ── API shim (→ global API registry) ────────────────────
    // NFS API shim for @backstage-community/plugin-shortcuts (v0.3.28–0.20.0).
    // The plugin has no NFS alpha export, so we register its API factory manually
    // under the 'app' pluginId (createFrontendModule targeting a non-existent
    // pluginId is silently dropped by NFS).
    // The <Shortcuts> component in Sidebar.tsx calls useApi(shortcutsApiRef) —
    // this is the only registration it needs.
    // Remove this shim when the plugin adds NFS support:
    // https://github.com/backstage/community-plugins/tree/main/workspaces/shortcuts
    ApiBlueprint.make({
      name: 'shortcuts',
      params: defineParams =>
        defineParams(
          createApiFactory({
            api: shortcutsApiRef,
            deps: { storageApi: storageApiRef },
            factory: ({ storageApi }) =>
              new DefaultShortcutsApi(storageApi.forBucket('shortcuts')),
          }),
        ),
    }),
  ],
});
